/**
 * Rate Limiter for Upload Operations
 *
 * Implements per-contributor rate limiting to prevent abuse.
 * Rate limits are stored in localStorage and reset after the cooldown period.
 */

// Rate limit configuration
export const RATE_LIMIT_CONFIG = {
  // Maximum uploads per window
  maxUploads: 10,
  // Time window in milliseconds (1 minute)
  windowMs: 60 * 1000,
  // Cooldown period after hitting limit (30 seconds)
  cooldownMs: 30 * 1000,
}

// Storage key for rate limit data
const RATE_LIMIT_STORAGE_KEY = 'photodrop_rate_limits'

// Rate limit entry for a contributor session
interface RateLimitEntry {
  // Timestamps of recent uploads
  uploadTimestamps: number[]
  // When the limit was last hit (for cooldown)
  limitHitAt?: number
}

// Rate limit state by session ID
interface RateLimitStore {
  [sessionId: string]: RateLimitEntry
}

// Get session ID for rate limiting (uses same session as view tracking)
function getSessionId(): string {
  let sessionId = sessionStorage.getItem('photodrop_session_id')
  if (!sessionId) {
    sessionId = crypto.randomUUID()
    sessionStorage.setItem('photodrop_session_id', sessionId)
  }
  return sessionId
}

// Get rate limit store from localStorage
function getRateLimitStore(): RateLimitStore {
  const stored = localStorage.getItem(RATE_LIMIT_STORAGE_KEY)
  if (stored) {
    try {
      return JSON.parse(stored)
    } catch {
      return {}
    }
  }
  return {}
}

// Save rate limit store to localStorage
function saveRateLimitStore(store: RateLimitStore): void {
  localStorage.setItem(RATE_LIMIT_STORAGE_KEY, JSON.stringify(store))
}

// Clean up old entries from a rate limit entry
function cleanupEntry(entry: RateLimitEntry): RateLimitEntry {
  const now = Date.now()
  const windowStart = now - RATE_LIMIT_CONFIG.windowMs

  return {
    ...entry,
    uploadTimestamps: entry.uploadTimestamps.filter(ts => ts > windowStart),
    // Clear limitHitAt if cooldown has passed
    limitHitAt: entry.limitHitAt && (now - entry.limitHitAt > RATE_LIMIT_CONFIG.cooldownMs)
      ? undefined
      : entry.limitHitAt,
  }
}

// Check if current session can upload
export function checkRateLimit(): {
  allowed: boolean
  remaining: number
  resetIn: number | null
  message?: string
} {
  const sessionId = getSessionId()
  const store = getRateLimitStore()

  // Get or create entry for this session
  let entry = store[sessionId] || { uploadTimestamps: [] }

  // Clean up old timestamps
  entry = cleanupEntry(entry)

  const now = Date.now()

  // Check if in cooldown period
  if (entry.limitHitAt) {
    const cooldownRemaining = RATE_LIMIT_CONFIG.cooldownMs - (now - entry.limitHitAt)
    if (cooldownRemaining > 0) {
      return {
        allowed: false,
        remaining: 0,
        resetIn: Math.ceil(cooldownRemaining / 1000),
        message: `Rate limit exceeded. Please wait ${Math.ceil(cooldownRemaining / 1000)} seconds before uploading again.`,
      }
    }
    // Cooldown has passed, clear it
    entry.limitHitAt = undefined
  }

  // Check upload count in current window
  const uploadsInWindow = entry.uploadTimestamps.length
  const remaining = Math.max(0, RATE_LIMIT_CONFIG.maxUploads - uploadsInWindow)

  if (uploadsInWindow >= RATE_LIMIT_CONFIG.maxUploads) {
    // Find when the oldest upload will expire from the window
    const oldestTimestamp = Math.min(...entry.uploadTimestamps)
    const resetIn = Math.max(0, Math.ceil((oldestTimestamp + RATE_LIMIT_CONFIG.windowMs - now) / 1000))

    // Mark limit as hit
    entry.limitHitAt = now
    store[sessionId] = entry
    saveRateLimitStore(store)

    return {
      allowed: false,
      remaining: 0,
      resetIn,
      message: `Upload limit reached (${RATE_LIMIT_CONFIG.maxUploads} per minute). Please wait ${resetIn} seconds.`,
    }
  }

  // Calculate when window will reset (for display purposes)
  let resetIn: number | null = null
  if (entry.uploadTimestamps.length > 0) {
    const oldestTimestamp = Math.min(...entry.uploadTimestamps)
    resetIn = Math.max(0, Math.ceil((oldestTimestamp + RATE_LIMIT_CONFIG.windowMs - now) / 1000))
  }

  return {
    allowed: true,
    remaining,
    resetIn,
  }
}

// Record an upload (call this after successful upload)
export function recordUpload(count: number = 1): void {
  const sessionId = getSessionId()
  const store = getRateLimitStore()

  // Get or create entry for this session
  let entry = store[sessionId] || { uploadTimestamps: [] }

  // Clean up old timestamps
  entry = cleanupEntry(entry)

  // Record new uploads
  const now = Date.now()
  for (let i = 0; i < count; i++) {
    entry.uploadTimestamps.push(now)
  }

  // Save updated entry
  store[sessionId] = entry
  saveRateLimitStore(store)
}

// Get current rate limit status (for display)
export function getRateLimitStatus(): {
  uploadsUsed: number
  uploadsLimit: number
  remaining: number
  resetIn: number | null
  isLimited: boolean
} {
  const result = checkRateLimit()
  const sessionId = getSessionId()
  const store = getRateLimitStore()
  let entry = store[sessionId] || { uploadTimestamps: [] }
  entry = cleanupEntry(entry)

  return {
    uploadsUsed: entry.uploadTimestamps.length,
    uploadsLimit: RATE_LIMIT_CONFIG.maxUploads,
    remaining: result.remaining,
    resetIn: result.resetIn,
    isLimited: !result.allowed,
  }
}

// Clear rate limit data (for testing)
export function clearRateLimits(): void {
  localStorage.removeItem(RATE_LIMIT_STORAGE_KEY)
}
